import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { listInsights } from "@/lib/insights";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });

const CATEGORIES = [
  "All",
  "Institutional Security",
  "Digital Asset Custody",
  "Identity",
  "Tokenization",
  "Stablecoins",
  "Crypto Crime",
  "Quantum Security",
];

export default function Insights() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");

  useEffect(() => {
    // Insights are now bundled with the frontend — no network call.
    setItems(listInsights());
    setLoading(false);
  }, []);

  const filtered = useMemo(() => {
    if (active === "All") return items;
    return items.filter((i) => i.category === active);
  }, [items, active]);

  const featured = useMemo(() => filtered.find((i) => i.featured) || filtered[0], [filtered]);
  const rest = useMemo(
    () => (featured ? filtered.filter((i) => i.id !== featured.id) : filtered),
    [filtered, featured]
  );

  return (
    <main className="bg-white" data-testid="insights-page">
      <section className="pt-32 md:pt-40 pb-12 bg-radial-soft relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Insights
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-[#06102A] max-w-4xl">
              Thought leadership for regulated digital finance.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-[#06102A]/10 bg-white sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex gap-2 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              data-testid={`insights-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
                active === c
                  ? "bg-[#06102A] text-white"
                  : "bg-[#06102A]/[0.04] text-[#06102A]/70 hover:bg-[#06102A]/[0.08]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="insights-content">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          {loading && <p className="text-sm text-[#06102A]/60">Loading insights…</p>}

          {!loading && featured && (
            <Reveal>
              <Link
                to={`/insights/${featured.slug}`}
                data-testid="insights-featured"
                className="group grid grid-cols-1 md:grid-cols-12 gap-8 rounded-3xl overflow-hidden border border-[#06102A]/10 bg-white hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.25)] transition-all duration-500"
              >
                <div className="md:col-span-7 aspect-[16/10] md:aspect-auto relative overflow-hidden bg-[#06102A]/5">
                  {featured.image_url && (
                    <img
                      src={featured.image_url}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="md:col-span-5 p-8 md:p-10 flex flex-col justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#F97316]">
                      Featured · {featured.category}
                    </p>
                    <h2 className="mt-4 font-display text-3xl md:text-4xl font-medium leading-tight text-[#06102A]">
                      {featured.title}
                    </h2>
                    <p className="mt-4 text-[#06102A]/70 leading-relaxed">{featured.excerpt}</p>
                  </div>
                  <div className="mt-8 flex items-center justify-between text-xs text-[#06102A]/55">
                    <span className="inline-flex items-center gap-2">
                      <Clock className="h-3.5 w-3.5" /> {featured.read_minutes} min read
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#06102A] font-medium">
                      Read article <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((it, i) => (
              <Reveal key={it.id} delay={(i % 3) * 80}>
                <Link
                  to={`/insights/${it.slug}`}
                  data-testid={`insights-card-${it.slug}`}
                  className="group block rounded-2xl border border-[#06102A]/10 bg-white overflow-hidden hover:border-[#06102A] hover:-translate-y-0.5 hover:shadow-[0_30px_60px_-30px_rgba(6,16,42,0.2)] transition-all duration-500"
                >
                  <div className="aspect-[16/10] relative overflow-hidden bg-[#06102A]/5">
                    {it.image_url && (
                      <img
                        src={it.image_url}
                        alt={it.title}
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#06102A]/55">
                      {it.category}
                    </p>
                    <h3 className="mt-3 font-display text-xl font-medium leading-snug text-[#06102A]">
                      {it.title}
                    </h3>
                    <p className="mt-3 text-sm text-[#06102A]/70 line-clamp-2">{it.excerpt}</p>
                    <div className="mt-5 flex items-center justify-between text-xs text-[#06102A]/55">
                      <span>{fmtDate(it.published_at)}</span>
                      <span className="inline-flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" /> {it.read_minutes} min
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {!loading && filtered.length === 0 && (
            <p className="text-sm text-[#06102A]/60">No articles in this category yet.</p>
          )}
        </div>
      </section>
    </main>
  );
}
